"use client";

import React from "react";
import { Plus } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const playbookItems = [{
  index: "01",
  title: "Bathroom Remodel Timeline",
  steps: ["Bathroom remodel timelines can vary based on project size, materials, scheduling, and the work involved.", "Smaller bathroom updates may move faster than full bathroom remodel projects.", "Projects involving showers, tubs, tile, flooring, vanities, plumbing, or electrical work may require more planning.", "Material availability, permits, inspections, and scheduling can affect the overall timeline.", "A quote request is a good first step to review your project details and possible next steps."]
}, {
  index: "02",
  title: "Budgeting for a Bathroom Remodel",
  steps: ["Bathroom remodel costs can vary based on the size of the bathroom and the scope of the work.", "Common cost factors include showers, tubs, tile, flooring, vanities, fixtures, lighting, and layout changes.", "It is helpful to decide which updates matter most before requesting a quote.", "Cosmetic updates may cost less than projects involving plumbing, electrical, or layout changes.", "Requesting a quote can help you review possible project options, pricing factors, and next steps."]
}, {
  index: "03",
  title: "Choosing Bathroom Materials",
  steps: ["Bathroom remodel materials should be selected for moisture resistance, durability, and daily use.", "Common material choices include tile, flooring, vanities, countertops, sinks, fixtures, mirrors, and lighting.", "Slip-resistant flooring can be helpful in wet bathroom areas.", "Low-maintenance surfaces may make the bathroom easier to clean over time.", "Material options can depend on your style, budget, project scope, and bathroom layout."]
}, {
  index: "04",
  title: "Permits and Project Requirements",
  steps: ["Permit needs can vary depending on the bathroom remodel scope and local requirements.", "Projects involving plumbing, electrical, structural changes, or major layout updates may require permits.", "Simple cosmetic updates may not require the same level of permitting as larger remodel projects.", "It is important to review project details before work begins.", "A quote request can help identify whether your bathroom remodel may need additional planning."]
}, {
  index: "05",
  title: "Bathroom Layout and Storage Ideas",
  steps: ["Bathroom remodels can improve layout, storage, lighting, and daily function.", "Common updates include walk-in showers, tub replacements, new vanities, storage cabinets, and updated fixtures.", "Better lighting and mirror placement can make the bathroom feel brighter and more usable.", "Storage improvements can help reduce clutter and make the space easier to use.", "Layout changes can be helpful when the current bathroom feels cramped or outdated."]
}, {
  index: "06",
  title: "Maintaining Your Updated Bathroom",
  steps: ["Regular cleaning can help protect tile, grout, fixtures, mirrors, and bathroom surfaces.", "Bathroom ventilation can help reduce moisture buildup.", "Checking faucets, drains, and plumbing fixtures can help catch small issues early.", "Use cleaning products that are appropriate for your bathroom materials.", "Simple maintenance can help keep an updated bathroom looking clean and functional."]
}, {
  index: "07",
  title: "Getting Started",
  steps: ["Start by deciding what you want to update in your bathroom.", "Helpful details include whether you want a shower update, tub replacement, tile work, vanity update, flooring, or layout changes.", "Photos, measurements, and project notes can make it easier to discuss your remodel.", "You can call or submit the quote form to share your project details.", "After you request a quote, you can review possible next steps for your bathroom remodel."]
}];
export default function LakelandServiceAreaFAQ() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return <section data-section-id="1275" id="lakelandserviceareafaq" className="relative overflow-hidden bg-slate-50 py-32 text-slate-900 md:py-44" style={{
    clipPath: "polygon(0 7%, 100% 0, 100% 100%, 0 100%)"
  }}>
      <div ref={ref} className={`container mx-auto max-w-[1400px] px-6 transition-all duration-700 ease-out md:px-12 ${isVisible ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0"}`}>
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-4">
            <div className="lg:sticky lg:top-32">
              <div className="mb-8 flex items-center gap-3">
                <span className="block h-px w-6 bg-blue-600"></span>
                <span className="font-mono text-[0.75rem] uppercase tracking-[0.18em] text-slate-500">
                  Bathroom Remodel Guide
                </span>
              </div>

              <h2 className="mb-8 font-default text-[clamp(2.5rem,6vw,5.5rem)] uppercase leading-[0.92] tracking-[-0.005em] text-slate-900">
                Bathroom{" "}
                <em className="not-italic">
                  <span className="italic text-blue-600">Remodel</span>
                </em>
                .
              </h2>

              <p className="max-w-md font-default text-[clamp(1.0625rem,1.2vw,1.25rem)] leading-[1.6] text-slate-700">
                Everything you need to know about planning a bathroom remodel in
                Lakeland, FL.
              </p>

              <div className="mt-12 hidden font-mono text-[0.6875rem] uppercase tracking-[0.15em] text-slate-500 lg:block">
                Lakeland, Florida
              </div>
            </div>
          </div>

          <div className="lg:col-span-8">
            <Accordion type="single" collapsible className="w-full">
              {playbookItems.map((item, i) => <AccordionItem key={item.index} data-index={i} value={`item-${i}`} className="group border-b border-slate-200 last:border-b-0">
                  <AccordionTrigger className="py-0 hover:no-underline [&>svg]:hidden">
                    <div className="flex min-h-[96px] w-full items-center justify-between gap-6 py-6 transition-all duration-200 group-hover:pl-2">
                      <div className="flex flex-1 items-center gap-6 md:gap-10">
                        <span className="shrink-0 font-mono text-sm tracking-[0.1em] text-blue-600 md:text-base">
                          {item.index}
                        </span>

                        <h3 className="text-left font-default text-[clamp(1.25rem,2.4vw,2.25rem)] uppercase leading-[1] tracking-[0] text-slate-900 transition-transform duration-200 group-hover:translate-x-2">
                          {item.title}
                        </h3>
                      </div>

                      <span className="shrink-0 text-blue-600 transition-transform duration-300 group-data-[state=open]:rotate-45">
                        <Plus className="size-7 md:size-9" strokeWidth={1.75} />
                      </span>
                    </div>
                  </AccordionTrigger>

                  <AccordionContent className="pb-10 pl-0 md:pl-[5.5rem]">
                    <ol className="mb-8 max-w-2xl space-y-3">
                      {item.steps.map((step, sIdx) => <li key={sIdx} data-index={sIdx} className="flex gap-4 font-default text-base leading-[1.65] text-slate-600">
                          <span className="min-w-[2rem] shrink-0 pt-1 font-mono text-sm text-blue-600">
                            {String(sIdx + 1).padStart(2, "0")}.
                          </span>
                          <span>{step}</span>
                        </li>)}
                    </ol>

                    <Link to="/contact/#request-your-bathroom-remodel-quote" className="inline-flex items-center gap-2 font-mono text-[0.75rem] uppercase tracking-[0.18em] text-slate-900 underline decoration-1 underline-offset-[6px] transition-colors duration-200 hover:text-blue-600">
                      Request a Free Quote
                    </Link>
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>
        </div>
      </div>
    </section>;
}